// frontend/src/super-admin/access/AdminRolesPage.tsx
//
// Manage which super-admins have which platform roles. Owners see every
// admin and can re-assign roles; non-owners see read-only.

import * as React from 'react';
import { toast } from 'sonner';
import { ShieldCheck, ShieldAlert, Users, Lock, UserPlus, Mail } from 'lucide-react';
import { useAuth } from '../../core/auth/AuthProvider';
import { MfaSettingsCard } from '../../core/auth/MfaSettingsCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { PageHeader } from '../../design-system/components/PageHeader';
import { LoadingState } from '../../design-system/components/LoadingState';
import { ErrorState } from '../../design-system/components/ErrorState';
import { VISUAL, statusPill } from '../../design-system/visual';
import {
  accessApi,
  platformSettingsApi,
  type AdminPermission,
  type AdminRole,
  type AdminRoster,
  type PlatformSettings,
} from '../../modules/site-settings/api';
import { useAdminPermission } from '../../core/auth/useAdminPermission';
import { useConfirm } from '../../components/ui/confirm-dialog';

export function AdminRolesPage() {
  const confirm = useConfirm();
  const canWrite = useAdminPermission('admins.write');
  const canRead  = useAdminPermission('admins.read');
  const { identity } = useAuth();
  const myId = identity?.type === 'super_admin' ? identity.id : null;

  const [roles, setRoles] = React.useState<AdminRole[] | null>(null);
  const [admins, setAdmins] = React.useState<AdminRoster[] | null>(null);
  const [permissions, setPermissions] = React.useState<AdminPermission[] | null>(null);
  const [rolePerms, setRolePerms] = React.useState<Record<string, string[]>>({});
  const [error, setError] = React.useState<string | null>(null);

  // Platform-wide "require 2FA for platform admins" switch.
  const [platform, setPlatform] = React.useState<PlatformSettings | null>(null);
  const [platformBusy, setPlatformBusy] = React.useState(false);

  // Per-admin assignment editor state.
  const [editing, setEditing] = React.useState<{ adminId: string; selected: Set<string> } | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [search, setSearch] = React.useState('');

  // Invite dialog state.
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [inviteForm, setInviteForm] = React.useState<{ email: string; name: string; roleIds: Set<string> }>({
    email: '', name: '', roleIds: new Set<string>(),
  });
  const [inviting, setInviting] = React.useState(false);

  function load() {
    setError(null);
    setRoles(null);
    setAdmins(null);
    setPermissions(null);

    Promise.all([accessApi.listRoles(), accessApi.listAdmins(), accessApi.listPermissions()])
      .then(([r, a, p]) => {
        setRoles(r);
        setAdmins(a);
        setPermissions(p);

        // Hydrate role → permissions map.
        Promise.all(r.map((role) => accessApi.rolePermissions(role.id).then((perms) => [role.id, perms] as const)))
          .then((entries) => {
            const map: Record<string, string[]> = {};
            for (const [id, perms] of entries) map[id] = perms;
            setRolePerms(map);
          });
      })
      .catch((e) => setError(String(e.message ?? e)));
  }

  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!canRead) return;
    platformSettingsApi.get().then(setPlatform).catch(() => undefined);
  }, [canRead]);

  async function toggleRequireAdminMfa() {
    if (!platform || platformBusy) return;
    const next = !platform.require_admin_mfa;
    setPlatformBusy(true);
    try {
      const updated = await platformSettingsApi.update({ require_admin_mfa: next });
      setPlatform(updated);
      toast.success(next ? '2FA now required for platform admins' : '2FA requirement removed');
    } catch (err: any) {
      toast.error('Update failed', { description: err.message ?? String(err) });
    } finally {
      setPlatformBusy(false);
    }
  }

  function startEdit(admin: AdminRoster) {
    setEditing({
      adminId: admin.id,
      selected: new Set(admin.roles.map((r) => r.id)),
    });
  }

  function toggleSelected(roleId: string) {
    setEditing((prev) => {
      if (!prev) return prev;
      const next = new Set(prev.selected);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return { ...prev, selected: next };
    });
  }

  async function saveAssignment() {
    if (!editing) return;
    setBusy(true);
    try {
      const updated = await accessApi.setAdminRoles(editing.adminId, Array.from(editing.selected));
      setAdmins((prev) => (prev ? prev.map((a) => (a.id === updated.id ? updated : a)) : prev));
      toast.success('Roles updated');
      setEditing(null);
    } catch (err: any) {
      toast.error('Update failed', { description: err.message ?? String(err) });
    } finally { setBusy(false); }
  }

  async function toggleActive(target: AdminRoster) {
    const next = !target.is_active;
    if (next === false && !(await confirm({ title: `Suspend ${target.name}?`, description: "They won't be able to sign in.", destructive: true, confirmLabel: 'Suspend' }))) return;
    try {
      const updated = await accessApi.setAdminActive(target.id, next);
      setAdmins((prev) => (prev ? prev.map((a) => (a.id === updated.id ? updated : a)) : prev));
      toast.success(next ? 'Admin re-activated' : 'Admin suspended');
    } catch (err: any) {
      toast.error('Update failed', { description: err.message ?? String(err) });
    }
  }

  // Permanent, unlike Suspend. The confirm spells out what survives and what
  // does not, because "delete an admin" reads more destructive than it is: the
  // audit log keeps what they did (it stores a denormalised actor label and has
  // no foreign key to super_admins), and tenants they created are untouched.
  async function removeAdmin(target: AdminRoster) {
    const ok = await confirm({
      title: `Delete ${target.name}?`,
      description:
        `${target.email} is removed permanently and their platform roles go with them. ` +
        `This cannot be undone — use Suspend instead if you only want to block sign-in. ` +
        `The audit log keeps everything they did, and any tenants they created are unaffected.`,
      destructive: true,
      confirmLabel: 'Delete permanently',
    });
    if (!ok) return;
    try {
      await accessApi.deleteAdmin(target.id);
      setAdmins((prev) => (prev ? prev.filter((a) => a.id !== target.id) : prev));
      toast.success(`${target.name} deleted`);
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message ?? String(err) });
    }
  }

  function openInvite() {
    setInviteForm({ email: '', name: '', roleIds: new Set<string>() });
    setInviteOpen(true);
  }

  function toggleInviteRole(roleId: string) {
    setInviteForm((prev) => {
      const next = new Set(prev.roleIds);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return { ...prev, roleIds: next };
    });
  }

  async function submitInvite() {
    if (inviting) return;
    if (!inviteForm.email.trim() || !inviteForm.name.trim()) {
      toast.error('Email and name are required');
      return;
    }
    setInviting(true);
    try {
      const r = await accessApi.inviteAdmin({
        email: inviteForm.email.trim(),
        name: inviteForm.name.trim(),
        role_ids: Array.from(inviteForm.roleIds),
      });
      setAdmins((prev) => (prev ? [...prev, r.admin] : [r.admin]));
      toast.success('Invite sent', {
        description: `Magic link expires ${new Date(r.magicLinkExpiresAt).toLocaleString()}.`,
      });
      setInviteOpen(false);
    } catch (err: any) {
      toast.error('Invite failed', { description: err.message ?? String(err) });
    } finally { setInviting(false); }
  }

  if (!canRead) {
    return (
      <div className="px-6 sm:px-8 lg:px-10 py-16">
        <div className={[VISUAL.bento, 'p-8 max-w-md mx-auto text-center'].join(' ')}>
          <Lock className="h-6 w-6 text-coral mx-auto" strokeWidth={1.6} />
          <p className="mt-3 text-[16px] font-semibold text-foreground">No access</p>
          <p className="text-[13.5px] text-muted-foreground mt-1">You need admins.read permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (error) return <ErrorState description={error} onRetry={load} />;
  if (!roles || !admins || !permissions) return <LoadingState variant="page" />;

  const filteredAdmins = admins.filter((a) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q);
  });

  // Group permissions by area for the roles panel.
  const permsByArea = permissions.reduce<Record<string, AdminPermission[]>>((acc, p) => {
    (acc[p.area] ||= []).push(p);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader
        eyebrow="Access / RBAC"
        title="Roles & permissions"
        description="Platform-level RBAC. Site managers can edit blog, reviews, and SEO without seeing tenants, billing, or releases."
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-8">
        {/* Roles overview */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/15 ring-1 ring-coral/25">
              <ShieldCheck className="h-4 w-4 text-coral" strokeWidth={1.7} />
            </div>
            <h3 className={VISUAL.dashSection}>System roles</h3>
          </div>

          <ul className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {roles.map((role) => {
              const grantedKeys = rolePerms[role.id] ?? [];
              const isOwner = role.key === 'owner';
              return (
                <li key={role.id} className={[VISUAL.bento, 'p-6'].join(' ')}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[15px] font-semibold tracking-[-0.005em] text-foreground">{role.name}</p>
                      <p className="text-[12px] font-mono text-muted-foreground">{role.key}</p>
                    </div>
                    <span className={statusPill(isOwner ? 'sent' : 'info')}>
                      {role.permission_count} {role.permission_count === 1 ? 'permission' : 'permissions'}
                    </span>
                  </div>
                  {role.description && (
                    <p className="mt-3 text-[13px] text-muted-foreground leading-relaxed">{role.description}</p>
                  )}

                  <div className="mt-5 space-y-3">
                    {Object.entries(permsByArea).map(([area, list]) => {
                      const granted = list.filter((p) => grantedKeys.includes(p.key));
                      if (granted.length === 0 && !isOwner) return null;
                      return (
                        <div key={area}>
                          <p className={VISUAL.eyebrow}>{area}</p>
                          <ul className="mt-1.5 flex flex-wrap gap-1.5">
                            {(isOwner ? list : granted).map((p) => (
                              <li
                                key={p.key}
                                className="inline-flex items-center px-2.5 py-1 rounded-full bg-muted/60 text-[11.5px] font-mono text-foreground/80 ring-1 ring-border/40"
                              >
                                {p.key}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Admin roster */}
        <section>
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-lavender-100 ring-1 ring-lavender-200">
                <Users className="h-4 w-4 text-lavender-700" strokeWidth={1.7} />
              </div>
              <h3 className={VISUAL.dashSection}>Super-admins</h3>
            </div>
            <div className="flex items-center gap-2">
              {canWrite && (
                <Button onClick={openInvite} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
                  <UserPlus className="h-3.5 w-3.5" />
                  Invite admin
                </Button>
              )}
              <Input
                type="search"
                placeholder="Search by name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10 rounded-full bg-card/80 border-border/60 text-[13px] max-w-xs"
              />
            </div>
          </div>

          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <ul className="divide-y divide-border/40">
              {filteredAdmins.map((a) => (
                <li key={a.id} className="flex items-center gap-4 px-5 sm:px-6 py-4">
                  <div className="h-10 w-10 rounded-full bg-coral text-white flex items-center justify-center font-bold text-[13px] ring-1 ring-coral/30 shrink-0">
                    {(a.name || a.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold text-foreground truncate">{a.name}</p>
                    <p className="text-[12px] text-muted-foreground truncate">{a.email}</p>
                  </div>
                  <div className="hidden sm:flex flex-wrap gap-1.5 max-w-xs justify-end">
                    {a.roles.length === 0 ? (
                      <span className={statusPill('draft')}>no roles</span>
                    ) : (
                      a.roles.map((r) => (
                        <span key={r.id} className={statusPill(r.key === 'owner' ? 'sent' : 'info')}>
                          {r.name}
                        </span>
                      ))
                    )}
                  </div>
                  {canWrite && (
                    <>
                      <span
                        className={
                          'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.10em] ring-1 ' +
                          (a.is_active
                            ? 'bg-lime-neon/30 text-lime-700 ring-lime-700/20'
                            : 'bg-rose-500/15 text-rose-700 ring-rose-500/30')
                        }
                      >
                        {a.is_active ? 'Active' : 'Suspended'}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => startEdit(a)}
                        className="h-9 px-3 rounded-full text-[12.5px] font-semibold"
                      >
                        Edit roles
                      </Button>
                      {a.id !== myId && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => toggleActive(a)}
                          className={
                            'h-9 px-3 rounded-full text-[12.5px] font-semibold ' +
                            (a.is_active ? 'text-rose-700 hover:text-rose-800' : 'text-foreground')
                          }
                        >
                          {a.is_active ? 'Suspend' : 'Reactivate'}
                        </Button>
                      )}
                      {a.id !== myId && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removeAdmin(a)}
                          className="h-9 px-3 rounded-full text-[12.5px] font-semibold text-rose-700 hover:text-rose-800"
                        >
                          Delete
                        </Button>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Platform-wide 2FA enforcement */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-500/15 ring-1 ring-rose-500/25">
              <ShieldAlert className="h-4 w-4 text-rose-600" strokeWidth={1.7} />
            </div>
            <h3 className={VISUAL.dashSection}>Platform security</h3>
          </div>

          <div className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            {!platform ? (
              <p className="text-[13px] text-muted-foreground">Loading…</p>
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-semibold text-foreground">Require 2FA for platform admins</p>
                  <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-relaxed">
                    Admins without two-factor enrolled will be prompted to set it up before they can sign in.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={platform.require_admin_mfa}
                  onClick={toggleRequireAdminMfa}
                  disabled={!canWrite || platformBusy}
                  className={
                    'shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ' +
                    (platform.require_admin_mfa ? 'bg-coral' : 'bg-muted ring-1 ring-border/40')
                  }
                >
                  <span
                    className={
                      'inline-block h-5 w-5 rounded-full bg-white shadow-pill transition-transform ' +
                      (platform.require_admin_mfa ? 'translate-x-5' : 'translate-x-0.5')
                    }
                  />
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Own admin account security */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/15 ring-1 ring-coral/25">
              <ShieldCheck className="h-4 w-4 text-coral" strokeWidth={1.7} />
            </div>
            <h3 className={VISUAL.dashSection}>Your admin account security</h3>
          </div>
          <MfaSettingsCard />
        </section>
      </div>

      {/* Assignment drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setEditing(null)}
            className="absolute inset-0 bg-zinc-950/40 backdrop-blur-sm"
          />
          <div className="ml-auto relative w-full max-w-md h-full bg-card overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-md border-b border-border/40 px-6 py-4 flex items-center justify-between">
              <div>
                <p className={VISUAL.eyebrow}>Assign roles</p>
                <p className="mt-0.5 text-[16px] font-semibold text-foreground">
                  {admins.find((a) => a.id === editing.adminId)?.name}
                </p>
              </div>
              <Button type="button" variant="ghost" onClick={() => setEditing(null)} className="h-9 px-3 text-[13px] rounded-full">
                Cancel
              </Button>
            </div>

            <div className="p-6 space-y-3">
              <Label>Roles</Label>
              <ul className="space-y-2">
                {roles.map((r) => {
                  const checked = editing.selected.has(r.id);
                  return (
                    <li key={r.id}>
                      <label
                        className={
                          'flex items-start gap-3 p-4 rounded-2xl cursor-pointer transition-colors ' +
                          (checked
                            ? 'bg-coral/10 ring-1 ring-coral/30'
                            : 'bg-muted/40 ring-1 ring-border/40 hover:bg-muted/60')
                        }
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelected(r.id)}
                          className="sr-only"
                        />
                        <span
                          className={
                            'mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-md ring-1 shrink-0 ' +
                            (checked ? 'bg-coral text-white ring-coral' : 'bg-white ring-border')
                          }
                        >
                          {checked && (
                            <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3">
                              <path d="M2 6L5 9L10 3" />
                            </svg>
                          )}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-[14px] font-semibold text-foreground">{r.name}</span>
                          <span className="block text-[11.5px] font-mono text-muted-foreground">{r.key}</span>
                          {r.description && (
                            <span className="block mt-1 text-[12.5px] text-muted-foreground leading-relaxed">
                              {r.description}
                            </span>
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="sticky bottom-0 bg-card/95 backdrop-blur-md border-t border-border/40 px-6 py-4 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setEditing(null)} className="h-10 px-4 rounded-full text-[13px]">
                Cancel
              </Button>
              <Button type="button" onClick={saveAssignment} disabled={busy} className="h-10 px-5 rounded-full text-[13px] font-semibold">
                {busy ? 'Saving…' : 'Save roles'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invite drawer */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setInviteOpen(false)}
            className="absolute inset-0 bg-zinc-950/40 backdrop-blur-sm"
          />
          <div className="ml-auto relative w-full max-w-md h-full bg-card overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-md border-b border-border/40 px-6 py-4 flex items-center justify-between">
              <div>
                <p className={VISUAL.eyebrow}>Invite admin</p>
                <p className="mt-0.5 text-[16px] font-semibold text-foreground">New super-admin</p>
              </div>
              <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)} className="h-9 px-3 text-[13px] rounded-full">
                Cancel
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="jane@worqly.io"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Full name *</Label>
                <Input
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  placeholder="Jane Operator"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label>Roles</Label>
                <p className="text-[12px] text-muted-foreground mt-1">
                  Pick what they can do. Site manager = blog + reviews + SEO only.
                </p>
                <ul className="mt-2 space-y-2">
                  {(roles ?? []).map((r) => {
                    const checked = inviteForm.roleIds.has(r.id);
                    return (
                      <li key={r.id}>
                        <label
                          className={
                            'flex items-start gap-3 p-3 rounded-2xl cursor-pointer transition-colors ' +
                            (checked
                              ? 'bg-coral/10 ring-1 ring-coral/30'
                              : 'bg-muted/40 ring-1 ring-border/40 hover:bg-muted/60')
                          }
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleInviteRole(r.id)}
                            className="sr-only"
                          />
                          <span
                            className={
                              'mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-md ring-1 shrink-0 ' +
                              (checked ? 'bg-coral text-white ring-coral' : 'bg-white ring-border')
                            }
                          >
                            {checked && (
                              <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3">
                                <path d="M2 6L5 9L10 3" />
                              </svg>
                            )}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-[13.5px] font-semibold text-foreground">{r.name}</span>
                            <span className="block text-[11px] font-mono text-muted-foreground">{r.key}</span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="rounded-2xl bg-muted/40 ring-1 ring-border/40 p-3 flex items-start gap-2.5">
                <Mail className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-[12.5px] text-muted-foreground leading-relaxed">
                  We'll email a magic link to the address above. They have 15 minutes to set a
                  password and sign in.
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-card/95 backdrop-blur-md border-t border-border/40 px-6 py-4 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)} className="h-10 px-4 rounded-full text-[13px]">
                Cancel
              </Button>
              <Button type="button" onClick={submitInvite} disabled={inviting} className="h-10 px-5 rounded-full text-[13px] font-semibold">
                {inviting ? 'Sending invite…' : 'Send invite'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
